import { createClient } from 'pexels';
import { backupImages } from '../data/imageDatabase';

const client = createClient(process.env.REACT_APP_PEXELS_API_KEY);
const usedImagesInCurrentGame = new Set();
const usedAnswersInCurrentRound = new Set();
const usedAnswersInCurrentGame = new Set();

export const categories = {
  animals: {
    name: "Animals",
    icon: "🦁",
    searchTerms: [
      { 
        query: "male lion face close up mane", 
        answer: "lion",
        alternateAnswers: ["male lion"],
        difficulty: "easy"
      },
      { 
        query: "bengal tiger face close up orange", 
        answer: "tiger",
        alternateAnswers: ["bengal tiger"],
        difficulty: "easy"
      },
      { 
        query: "african elephant trunk tusks", 
        answer: "elephant",
        alternateAnswers: ["african elephant"],
        difficulty: "medium"
      },
      { 
        query: "giraffe long neck spots", 
        answer: "giraffe",
        alternateAnswers: ["african giraffe"],
        difficulty: "medium"
      }
    ]
  },
  food: {
    name: "Food",
    icon: "🍕",
    searchTerms: [
      { 
        query: "whole pepperoni pizza on table", 
        answer: "pizza",
        alternateAnswers: ["pepperoni pizza"],
        difficulty: "easy"
      },
      { 
        query: "cheeseburger with fries plate", 
        answer: "burger",
        alternateAnswers: ["hamburger", "cheeseburger"],
        difficulty: "easy"
      },
      { 
        query: "chocolate cake slice dessert", 
        answer: "cake",
        alternateAnswers: ["chocolate cake"],
        difficulty: "medium"
      }
    ]
  },
  landmarks: {
    name: "Landmarks",
    icon: "🗽",
    searchTerms: [
      { 
        query: "eiffel tower paris full structure", 
        answer: "eiffel tower",
        alternateAnswers: ["tour eiffel"],
        difficulty: "easy"
      },
      { 
        query: "taj mahal agra white marble", 
        answer: "taj mahal",
        alternateAnswers: ["the taj mahal"],
        difficulty: "medium"
      },
      { 
        query: "pyramids of giza sphinx desert", 
        answer: "pyramids",
        alternateAnswers: ["egyptian pyramids", "pyramid"],
        difficulty: "easy"
      }
    ]
  },
  nature: {
    name: "Nature",
    icon: "🌲",
    searchTerms: [
      { 
        query: "large waterfall cascade nature", 
        answer: "waterfall",
        alternateAnswers: ["waterfalls", "falls"],
        difficulty: "easy"
      },
      { 
        query: "tropical beach palm trees ocean", 
        answer: "beach",
        alternateAnswers: ["sandy beach", "tropical beach"],
        difficulty: "easy"
      },
      { 
        query: "snow covered mountain peak alps", 
        answer: "mountain",
        alternateAnswers: ["mountains", "snowy mountain"],
        difficulty: "medium"
      }
    ]
  },
  vehicles: {
    name: "Vehicles",
    icon: "🚗",
    searchTerms: [
      { 
        query: "red ferrari sports car side view", 
        answer: "ferrari",
        alternateAnswers: ["sports car", "red ferrari"],
        difficulty: "easy"
      },
      { 
        query: "commercial airplane flying blue sky", 
        answer: "airplane",
        alternateAnswers: ["plane", "aircraft"],
        difficulty: "easy"
      },
      { 
        query: "steam locomotive train front view", 
        answer: "train",
        alternateAnswers: ["locomotive", "steam train"],
        difficulty: "medium"
      }
    ]
  },
  space: {
    name: "Space",
    icon: "🚀",
    searchTerms: [
      { 
        query: "full moon clear night sky", 
        answer: "moon",
        alternateAnswers: ["full moon"],
        difficulty: "easy"
      },
      { 
        query: "saturn planet rings space", 
        answer: "saturn",
        alternateAnswers: ["planet saturn", "ringed planet"]
      },
      { 
        query: "spiral galaxy in space", 
        answer: "galaxy",
        alternateAnswers: ["spiral galaxy", "milky way"]
      },
      { 
        query: "nebula space colorful", 
        answer: "nebula",
        alternateAnswers: ["space nebula"],
        difficulty: "medium"
      },
      { 
        query: "quasar space phenomenon", 
        answer: "quasar",
        alternateAnswers: ["space quasar"],
        difficulty: "hard"
      },
      { 
        query: "astronaut spacewalk earth background", 
        answer: "astronaut",
        alternateAnswers: ["spaceman", "space walker"]
      }
    ]
  },
  sports: {
    name: "Sports",
    icon: "⚽",
    searchTerms: [
      { 
        query: "soccer football stadium field empty", 
        answer: "soccer field",
        alternateAnswers: ["football field", "soccer pitch", "football pitch"]
      },
      { 
        query: "basketball court indoor empty", 
        answer: "basketball court",
        alternateAnswers: ["basketball", "court"]
      },
      { 
        query: "tennis court net empty", 
        answer: "tennis court",
        alternateAnswers: ["tennis", "tennis stadium"]
      },
      { 
        query: "baseball diamond field empty", 
        answer: "baseball field",
        alternateAnswers: ["baseball diamond", "baseball stadium"]
      }
    ]
  }
};

export const resetUsedImages = () => {
  usedImagesInCurrentGame.clear();
  usedAnswersInCurrentGame.clear();
};

export const resetRound = () => {
  usedAnswersInCurrentRound.clear();
};

// Add backup image function
const getBackupImage = (answer, category) => {
  const categoryBackups = backupImages[category.toLowerCase()];
  if (!categoryBackups) return null;

  const imageList = categoryBackups[answer.toLowerCase()];
  if (!imageList || imageList.length === 0) return null;

  // Get random backup image
  const randomIndex = Math.floor(Math.random() * imageList.length);
  return imageList[randomIndex];
};

// Update getRandomImage to use backup
export const getRandomImage = async (categoryIds = null, difficulty = 'any') => {
  try {
    let selectedCategory;
    if (categoryIds) {
      const categoryList = categoryIds.split(',');
      selectedCategory = categories[categoryList[Math.floor(Math.random() * categoryList.length)]];
    } else {
      const allCategories = Object.values(categories);
      selectedCategory = allCategories[Math.floor(Math.random() * allCategories.length)];
    }

    // Filter out search terms with already used answers
    let availableSearchTerms = selectedCategory.searchTerms.filter(
      term => !usedAnswersInCurrentRound.has(term.answer) && 
             !usedAnswersInCurrentGame.has(term.answer)
    );

    if (difficulty !== 'any') {
      availableSearchTerms = availableSearchTerms.filter(
        term => term.difficulty === difficulty
      );
    }

    if (availableSearchTerms.length === 0) {
      console.warn('No more unused answers in category');
      return null;
    }

    const searchTerm = availableSearchTerms[
      Math.floor(Math.random() * availableSearchTerms.length)
    ];

    // Try Pexels API first
    try {
      const result = await client.photos.search({
        query: searchTerm.query,
        per_page: 80,
        orientation: 'landscape',
        size: 'medium',
        min_width: 800,
        min_height: 600,
        quality: 1
      });

      if (result.photos && result.photos.length > 0) {
        // Filter out potentially problematic images
        const validPhotos = result.photos.filter(photo => {
          // Check if image exists and has required properties
          if (!photo.src || !photo.src.large) return false;
          
          // Verify image URL is accessible
          const img = new Image();
          img.src = photo.src.large;
          return true;
        }).filter(photo => !usedImagesInCurrentGame.has(photo.id));

        if (validPhotos.length === 0) {
          console.warn('No valid photos found, trying backup...');
          usedAnswersInCurrentRound.add(searchTerm.answer);
          return getRandomImage(categoryIds, difficulty);
        }

        const randomPhoto = validPhotos[
          Math.floor(Math.random() * validPhotos.length)
        ];

        // Preload the image
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            usedImagesInCurrentGame.add(randomPhoto.id);
            usedAnswersInCurrentRound.add(searchTerm.answer);
            usedAnswersInCurrentGame.add(searchTerm.answer);

            resolve({
              url: randomPhoto.src.large,
              answer: searchTerm.answer,
              alternateAnswers: searchTerm.alternateAnswers,
              photographer: randomPhoto.photographer,
              photographerUrl: randomPhoto.photographer_url,
              category: selectedCategory.name,
              difficulty: searchTerm.difficulty
            });
          };
          img.onerror = () => {
            console.warn('Image failed to load, trying backup...');
            usedImagesInCurrentGame.add(randomPhoto.id);
            resolve(getRandomImage(categoryIds, difficulty));
          };
          img.src = randomPhoto.src.large;
        });
      }
    } catch (pexelsError) {
      console.warn('Pexels API failed, trying backup images:', pexelsError);
    }

    // If Pexels fails or returns no results, try backup
    const backupImage = getBackupImage(searchTerm.answer, selectedCategory.name);
    if (backupImage) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          usedAnswersInCurrentRound.add(searchTerm.answer);
          usedAnswersInCurrentGame.add(searchTerm.answer);
          
          resolve({
            url: backupImage.url,
            answer: searchTerm.answer,
            alternateAnswers: searchTerm.alternateAnswers,
            photographer: backupImage.photographer,
            photographerUrl: backupImage.photographerUrl,
            category: selectedCategory.name,
            difficulty: searchTerm.difficulty,
            isBackup: true
          });
        };
        img.onerror = () => {
          console.warn('Backup image failed to load, trying another term');
          usedAnswersInCurrentRound.add(searchTerm.answer);
          resolve(getRandomImage(categoryIds, difficulty));
        };
        img.src = backupImage.url;
      });
    }

    // If both Pexels and backup fail, try another term
    console.warn('No images available for', searchTerm.answer);
    usedAnswersInCurrentRound.add(searchTerm.answer);
    return getRandomImage(categoryIds, difficulty);

  } catch (error) {
    console.error('Error in getRandomImage:', error);
    return null;
  }
};

// Add a function to check collection sizes
export const checkCollectionSizes = async () => {
  console.log("Checking collection sizes...");
  
  for (const [categoryName, category] of Object.entries(categories)) {
    console.log(`\n${categoryName.toUpperCase()}:`);
    
    for (const searchTerm of category.searchTerms) {
      try {
        const result = await client.photos.search({
          query: searchTerm.query,
          per_page: 80,
          orientation: 'landscape'
        });
        
        console.log(`${searchTerm.query}: ${result.photos.length} photos`);
      } catch (error) {
        console.log(`${searchTerm.query}: Error fetching (${error.message})`);
      }
    }
  }
};

// Add this test function
export const testImageQueries = async () => {
  console.log("Testing image queries...");
  const results = {};

  for (const [categoryName, category] of Object.entries(categories)) {
    results[categoryName] = [];
    console.log(`\nTesting ${categoryName}...`);
    
    for (const searchTerm of category.searchTerms) {
      try {
        const result = await client.photos.search({
          query: searchTerm.query,
          per_page: 80,
          orientation: 'landscape'
        });
        
        const status = {
          query: searchTerm.query,
          answer: searchTerm.answer,
          count: result.photos.length,
          hasImages: result.photos.length > 0,
          sampleUrl: result.photos[0]?.src.large || 'No image'
        };

        results[categoryName].push(status);
        console.log(`${searchTerm.answer}: ${status.count} photos found`);
      } catch (error) {
        console.error(`Error testing ${searchTerm.answer}: ${error.message}`);
      }
    }
  }

  return results;
}; 